import React from 'react';
import { Users, MessageCircle, Heart, Shield } from 'lucide-react';

const TIPS = [
  'Start each session with a brief check-in (how is everyone doing?)',
  'Review the previous rule briefly before introducing the new one',
  'Read key sections aloud—not everyone may read ahead',
  'Allow time for worksheet completion during group time',
  'Use open-ended questions: "What stood out to you?"',
  'Share your own experiences when appropriate—it builds trust',
  'Be prepared for emotional moments, especially around money stories',
  'End with encouragement and a specific action item for the week',
  'Remind participants this is education, not professional advice',
  'Celebrate progress—even small wins matter',
];

const AGREEMENTS = [
  { icon: Shield, title: 'Confidentiality', body: "What's shared in the group stays in the group. Personal financial details are private." },
  { icon: Heart, title: 'No Shame, No Judgment', body: 'There is no "right" or "wrong" past—only the opportunity to make better choices going forward.' },
  { icon: Users, title: 'Respectful Discussion', body: 'Listen without interrupting. Share your own experiences rather than giving unsolicited advice.' },
  { icon: MessageCircle, title: 'Participation is Voluntary', body: 'No one is required to share. Worksheets are personal and do not need to be shared.' },
];

const STRUCTURE = [
  ['Check-in / Wins from the week', '10 min'],
  ['Review previous rule / Q&A', '10 min'],
  ['Read and discuss new rule', '20 min'],
  ['Worksheet time', '15 min'],
  ['Group discussion / Sharing', '15 min'],
  ['Closing / Action items', '5 min'],
];

const SECTIONS = [
  ['Section I: Stability', 'Income tracking, multiple accounts, bill planning, spending reduction'],
  ['Section II: Control', 'Emergency funds, irregular expenses, lump sums, debt snowball'],
  ['Section III: Growth', 'Credit understanding, housing decisions, smart borrowing'],
  ['Section IV: Healing & Purpose', 'Money stories, values alignment, entrepreneurship basics'],
  ['Section V: Capstone', 'Personal Life & Money Map, commitment statement'],
  ['Section VI: Appendices', 'Glossary, trackers, worksheets, crisis mode rules'],
];

function FacilitatorGuideContent() {
  return (
    <div className="space-y-10" data-testid="facilitator-guide-content">
      {/* Program Description */}
      <section>
        <h2 className="font-serif text-2xl font-bold flex items-center gap-2 mb-2">
          <Users className="h-6 w-6 text-primary" /> Program Description
        </h2>
        <p className="text-sm text-muted-foreground mb-4">For group facilitators and organizations</p>
        <p className="text-sm text-foreground mb-3">
          <strong>The Rules of Money Workbook</strong> is a comprehensive financial literacy program for
          adults and teens of all backgrounds, written at a 7th-grade reading level — accessible yet
          thorough. It takes a trauma-informed, shame-free approach: money is emotional, many people carry
          financial trauma, and judgment-free education drives lasting change.
        </p>

        <h3 className="font-semibold mt-6 mb-3">Program Structure</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {SECTIONS.map(function (s) {
            return (
              <div key={s[0]} className="border border-border rounded-lg p-3">
                <p className="font-semibold text-sm">{s[0]}</p>
                <p className="text-xs text-muted-foreground mt-1">{s[1]}</p>
              </div>
            );
          })}
        </div>

        <h3 className="font-semibold mt-6 mb-3">Recommended Use</h3>
        <ul className="text-sm space-y-1.5 text-foreground">
          <li>• <strong>Individual study:</strong> self-paced, 1–2 rules per week</li>
          <li>• <strong>Group classes:</strong> 12–16 week program, 1 rule per session</li>
          <li>• <strong>Workshop format:</strong> weekend intensive covering key sections</li>
          <li>• <strong>Support groups:</strong> weekly meetings to discuss progress</li>
        </ul>
      </section>

      <div className="h-px bg-border" />

      {/* Facilitator Note */}
      <section>
        <h2 className="font-serif text-2xl font-bold flex items-center gap-2 mb-2">
          <MessageCircle className="h-6 w-6 text-primary" /> Facilitator Note
        </h2>
        <p className="text-sm text-muted-foreground mb-4">Guidelines for leading group sessions</p>
        <p className="text-sm text-foreground mb-4">
          Your role is not to be a financial expert, but to create a safe, supportive space where
          participants learn and grow together. The workbook does the teaching — you facilitate the conversation.
        </p>

        <h3 className="font-semibold mb-3">Group Agreements</h3>
        <div className="space-y-3">
          {AGREEMENTS.map(function (a) {
            const Icon = a.icon;
            return (
              <div key={a.title} className="flex items-start gap-3 bg-muted/30 rounded-lg p-3">
                <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">{a.title}</p>
                  <p className="text-xs text-muted-foreground">{a.body}</p>
                </div>
              </div>
            );
          })}
        </div>

        <h3 className="font-semibold mt-6 mb-3">Facilitator Tips</h3>
        <ol className="space-y-2">
          {TIPS.map(function (tip, i) {
            return (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                  {i + 1}
                </span>
                <span>{tip}</span>
              </li>
            );
          })}
        </ol>

        <h3 className="font-semibold mt-6 mb-3">Suggested 75-Minute Session</h3>
        <div className="bg-secondary/50 rounded-xl p-4 space-y-2 text-sm max-w-md">
          {STRUCTURE.map(function (row) {
            return (
              <div key={row[0]} className="flex justify-between">
                <span>{row[0]}</span>
                <span className="text-muted-foreground">{row[1]}</span>
              </div>
            );
          })}
          <div className="h-px bg-border my-1" />
          <div className="flex justify-between font-semibold">
            <span>Total</span><span>~75 min</span>
          </div>
        </div>
      </section>
    </div>
  );
}

export { FacilitatorGuideContent };
